"use client";

import { motion } from "framer-motion";
import {
  MicIcon,
  MicOffIcon,
  PhoneIcon,
  ArrowRightLeftIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { fetchUsers } from "@modules/sales/inbox/api";

interface User {
  id: string;
  name: string;
}

interface CallPopupProps {
  callerName: string;
  onAccept: () => void;
  onReject: () => void;
  onHangUp: () => void;
  onTransfer?: (userId: string) => void;
  usersToken?: string;
  supplierUuid?: string;
  muteCall: () => void;
  unMuteCall: () => void;
  isOutgoing?: boolean;
}

export const CallPopup = ({
  callerName,
  onAccept,
  onReject,
  onHangUp,
  onTransfer,
  usersToken,
  supplierUuid,
  muteCall,
  unMuteCall,
  isOutgoing = false,
}: CallPopupProps) => {
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [callDuration, setCallDuration] = useState<number>(0);
  const [isCallActive, setIsCallActive] = useState<boolean>(isOutgoing);

  const [users, setUsers] = useState<User[]>([]);
  const [isTransferring, setIsTransferring] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<string>("");

  useEffect(() => {
    if (!isOutgoing && usersToken && supplierUuid) {
      const loadUsers = async () => {
        const { status, data } = await fetchUsers(usersToken, supplierUuid);
        if (status) {
          setUsers(data);
        }
      };
      loadUsers();
    }
  }, [usersToken, supplierUuid, isOutgoing]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;

    if (isCallActive) {
      interval = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    }

    return () => {
      clearInterval(interval);
    };
  }, [isCallActive]);

  const handleMute = () => {
    setIsMuted((prevState) => !prevState);
  };

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed flex flex-col gap-4 p-4 bg-dark-blue border-dark-blue shadow-[0px_0px_3.5px_#D9D9D9] rounded-xl bottom-4 right-4 w-80"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">
          {isOutgoing ? "Outgoing Call" : "Incoming Call"}
        </h3>
        {isCallActive && (
          <span className="text-sm text-gray-300">
            {formatDuration(callDuration)}
          </span>
        )}
      </div>
      <p className="text-sm text-gray-300">
        {isOutgoing ? "Calling to" : "Caller"}: {callerName}
      </p>
      <div
        className={`flex ${isOutgoing ? "justify-end gap-2" : "justify-between"}`}
      >
        {!isOutgoing && (
          <button
            onClick={() => {
              onAccept();
              setIsCallActive(true);
            }}
            className={`px-4 py-2 text-white transition rounded-lg ${
              isCallActive
                ? "bg-gray-300 hover:bg-gray-400 cursor-not-allowed"
                : "bg-green-500 hover:bg-green-600 cursor-pointer"
            }`}
            disabled={isCallActive}
          >
            <PhoneIcon className="w-5 h-5 text-white" />
          </button>
        )}
        <button
          onClick={() => {
            if (isMuted) {
              unMuteCall();
            } else {
              muteCall();
            }
            handleMute();
          }}
          className={`px-4 py-2 text-white transition rounded-lg ${
            isMuted
              ? "bg-gray-500 hover:bg-gray-600"
              : "bg-yellow-500 hover:bg-yellow-600"
          }`}
        >
          {isMuted ? (
            <MicOffIcon className="w-5 h-5 text-white" />
          ) : (
            <MicIcon className="w-5 h-5 text-white" />
          )}
        </button>
        <button
          onClick={() => {
            isCallActive ? onHangUp() : onReject();
            setIsCallActive(false);
            setCallDuration(0);
          }}
          className="px-4 py-2 text-white transition bg-red-500 rounded-lg hover:bg-red-600"
        >
          <PhoneIcon className="w-5 h-5 text-white rotate-[130deg]" />
        </button>
      </div>

      {!isOutgoing && isCallActive && users.length > 0 && (
        <div className="flex flex-col gap-2 mt-2">
          {isTransferring ? (
            <div className="flex items-center gap-2">
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="w-full px-2 py-1 text-sm bg-white rounded-lg text-dark-blue"
              >
                <option value="" disabled>
                  Select user
                </option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
              <button
                onClick={() => {
                  if (selectedUser && onTransfer) {
                    onTransfer(selectedUser);
                  }
                }}
                disabled={!selectedUser}
                className="px-3 py-1 text-sm text-white transition bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-50"
              >
                Transfer
              </button>
              <button
                onClick={() => setIsTransferring(false)}
                className="px-3 py-1 text-sm text-white transition bg-gray-500 rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsTransferring(true)}
              className="flex items-center justify-center w-full gap-2 px-4 py-2 text-sm text-white transition bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              <ArrowRightLeftIcon className="w-4 h-4" /> Transfer Call
            </button>
          )}
        </div>
      )}
    </motion.div>
  );
};